
import {Component, Vue, Watch} from 'vue-property-decorator';
import {IExternalData} from '@/interfaces/external-data';
import {rules} from '@/utils';
import {
  dispatchCreateExternalData,
  dispatchExternalDataFileUpload,
  dispatchImportExternalDataFile,
  dispatchReplaceExternalDataFile,
  dispatchUpdateExternalData,
} from '@/store/admin/actions';
import {readAdminExternalData, readAdminExternalDataList} from '@/store/admin/getters';
import {dispatchGetExternalData} from '@/store/main/actions';
import {readExternalData, readToken} from '@/store/main/getters';
import {api} from '@/helpers/api';
import DefaultHelp from '@/components/tooltips/DefaultHelp.vue';

@Component({
  components: {DefaultHelp},
})
export default class CRUDExternalData extends Vue {
  private CEDTitle: string = `${this.$t('crud.create')} ${this.$t('external_data')}`;
  private csvFile: any = null;
  private objectHeaders: any = [];
  private previewRows: any = [];
  private previewTotal: number = 0;
  private previewOptions: any = {page: 1, itemsPerPage: 10};
  private valid: boolean = true;
  private rules: any = rules;
  private externalDataID: number = -1;

  private externalData: IExternalData = {} as IExternalData;

  public async mounted() {
    this.externalDataID = this.$router.currentRoute.params.id as unknown as number;
    if (this.externalDataID > 0) {
      if (readAdminExternalDataList(this.$store).length === 0) {
        await dispatchGetExternalData(this.$store, this.externalDataID);
        this.externalData = await readExternalData(this.$store) as IExternalData;
      } else {
        this.externalData = await readAdminExternalData(this.$store)(+this.externalDataID) as IExternalData;
      }
      this.CEDTitle = `${this.$t('crud.update')} ${this.$t('external_data')}`;


      if (this.externalData && this.externalData.headers) {
        await this.externalData.headers.forEach((item) => {
          this.objectHeaders.push({text: item, align: 'start', sortable: false, value: item});
        });
        await this.loadPreviewRows();
      }
    } else {
      this.externalData = {} as IExternalData;
      this.externalDataID = 0;
    }
  }

  public beforeRouteLeave(to, from, next) {
    this.externalData = {} as IExternalData;
    this.CEDTitle = `${this.$t('crud.create')} ${this.$t('external_data')}`;
    this.externalDataID = 0;
    this.csvFile = null;
    this.objectHeaders = [];
    this.previewRows = [];
    this.previewTotal = 0;
    next();
  }

  private async saveData() {
    const form: any = this.$refs.form_crud_ed;
    if (await form.validate()) {
      if (this.externalDataID > 0 && this.csvFile) {
        if (await dispatchReplaceExternalDataFile(this.$store, {data: this.externalData, file: this.csvFile})) {
          this.$router.push({name: 'main-admin-external-data-list'});
        }
      } else if (this.externalDataID > 0) {
        if (await dispatchUpdateExternalData(this.$store, this.externalData)) {
          this.$router.push({name: 'main-admin-external-data-list'});
        }
      } else if (this.csvFile) {
        if (await dispatchImportExternalDataFile(this.$store, {data: this.externalData, file: this.csvFile})) {
          this.$router.push({name: 'main-admin-external-data-list'});
        }
      } else {
        if (await dispatchCreateExternalData(this.$store, this.externalData)) {
          this.$router.push({name: 'main-admin-external-data-list'});
        }
      }

    }
  }

  private cancel() {
    this.$router.back();
  }

  private reset() {
    // @ts-ignore
    this.$refs.form_crud_ed.reset();
  }

  private async loadFileInTable(val) {
    this.externalData.headers = [];
    this.externalData.values = [];
    this.objectHeaders = [];
    this.previewRows = [];
    this.previewTotal = 0;
    if (!this.csvFile) {
      console.log('No file selected');
      return;
    }

    if (await dispatchExternalDataFileUpload(this.$store, this.csvFile)) {
      const cExternalData = readExternalData(this.$store) as IExternalData;
      this.externalData.headers = cExternalData.headers;
      this.previewRows = cExternalData.values || [];
      this.previewTotal = cExternalData.total_rows || this.previewRows.length;
      if (this.externalData?.headers) {
        this.externalData.headers.forEach((item) => {
          this.objectHeaders.push({text: item, align: 'start', sortable: false, value: item});
        });
      }
    }

  }

  private async loadPreviewRows() {
    if (!this.externalDataID || this.externalDataID <= 0) {
      return;
    }
    const perPage = this.previewOptions.itemsPerPage || 10;
    const page = this.previewOptions.page || 1;
    const response = await api.getExternalDataRows(this.externalDataID, {
      skip: (page - 1) * perPage,
      limit: perPage,
    });
    this.previewRows = response.data.items.map((item) => item.value);
    this.previewTotal = response.data.total_items;
  }

  private async downloadExternalData(format: string) {
    const response = await api.downloadExternalData(readToken(this.$store), this.externalDataID, format);
    const type = response.headers['content-type'];
    // @ts-ignore
    const blob = new Blob([response.data], {type, encoding: 'UTF-8'});
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = response.headers?.filename || `${this.safeFileName(this.externalData.name)}.${format}`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  private safeFileName(value: string) {
    return (value || 'dados_externos').replace(/[^a-zA-Z0-9_.-]+/g, '_');
  }

  @Watch('watchPreviewOptions')
  private async previewOptionsWatch() {
    await this.loadPreviewRows();
  }

  private get watchPreviewOptions() {
    return JSON.stringify(this.previewOptions);
  }
}
